import { formatDistanceStrict } from 'date-fns';

/**
 * Every time Toado shows, in the person's own time zone (TOADO-904).
 *
 * "just now" and "3 hours ago" never said when something happened in
 * someone's day, and about thirty places formatted dates their own way: in
 * the browser's zone with its default format, forced to UTC, or a date with
 * no time. Everything goes through here instead.
 *
 * The zone is the one saved on the person's account (`/me`), which the
 * AuthProvider hands to `setDisplayTimeZone`; until then, and for anyone
 * signed out, it is the browser's. The clock and the date order follow the
 * browser's locale.
 *
 * | When it happened  | Shown as             |
 * | ----------------- | -------------------- |
 * | Today             | 2:41 PM              |
 * | Yesterday         | Yesterday 4:05 PM    |
 * | Tomorrow          | Tomorrow 9:00 AM     |
 * | This year         | Oct 18, 4:05 PM      |
 * | Another year      | Oct 18, 2025         |
 *
 * A ticket's times are `exact`: the date and the time whenever it was, "Oct
 * 22, 2:41 PM" today too and "Oct 18, 2025, 4:05 PM" in another year,
 * because a bug is compared with when a fix went out (Jason, 22 Oct).
 *
 * `formatFull` is the hover: "Wed, Oct 21, 2026, 2:41 PM EDT". Durations
 * ("waiting 3 days") stay durations, through `durationSince`.
 */

let chosenZone: string | null = null;

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Whether `zone` is an IANA time zone this browser knows. */
export function isKnownTimeZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || !/^[A-Za-z0-9_+\-/]{1,64}$/.test(zone)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * A zone by its canonical name ("US/Eastern" and "america/new_york" are
 * America/New_York), or null when it is not one: names are compared, and an
 * alias of the same zone is the same zone (z.ai, round 1).
 */
export function canonicalTimeZone(zone: unknown): string | null {
  if (!isKnownTimeZone(zone)) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

/** The zone saved on the person's account, or null to follow the browser. */
export function setDisplayTimeZone(zone: string | null | undefined): void {
  chosenZone = canonicalTimeZone(zone);
}

export function displayTimeZone(): string {
  return chosenZone ?? browserTimeZone();
}

type When = string | number | Date;

function toDate(at: When): Date {
  return at instanceof Date ? at : new Date(at);
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone and options: a board renders hundreds of times. */
function formatter(zone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${zone}|${JSON.stringify(options)}`;
  let found = formatters.get(key);
  if (!found) {
    found = new Intl.DateTimeFormat(undefined, { ...options, timeZone: zone });
    formatters.set(key, found);
  }
  return found;
}

const dayFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * The calendar day `date` falls on in `zone`, as numbers. Read in en-US with
 * Latin digits whatever the browser's locale, since the parts are parsed.
 */
function dayIn(date: Date, zone: string): { year: number; month: number; day: number } {
  let days = dayFormatters.get(zone);
  if (!days) {
    days = new Intl.DateTimeFormat('en-US', { timeZone: zone, numberingSystem: 'latn', year: 'numeric', month: 'numeric', day: 'numeric' });
    dayFormatters.set(zone, days);
  }
  const parts = days.formatToParts(date);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: read('year'), month: read('month'), day: read('day') };
}

/** Whole calendar days from `now` to `date` in `zone`: 0 today, -1 yesterday, 1 tomorrow. */
function daysFrom(date: Date, now: Date, zone: string): number {
  const a = dayIn(date, zone);
  const b = dayIn(now, zone);
  return Math.round((Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day)) / 86_400_000);
}

const TIME: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

export interface FormatOptions {
  /** The moment to measure against; now by default. */
  now?: Date;
  /** The zone to show it in; the person's by default. */
  zone?: string;
  /** Leave the time out for days other than today (a card's corner). */
  dateOnly?: boolean;
  /** Keep the time for another year too (a reset, whose hour is the point). */
  withTime?: boolean;
  /** Always the date and the time, never "Yesterday" or the time alone (a ticket's times). */
  exact?: boolean;
}

/** A time as the table above says, in the person's zone. */
export function formatWhen(at: When, options: FormatOptions = {}): string {
  const date = toDate(at);
  if (Number.isNaN(date.getTime())) return '';
  const zone = options.zone ?? displayTimeZone();
  const now = options.now ?? new Date();
  const time = formatter(zone, TIME).format(date);
  if (options.exact) {
    const sameYear = dayIn(date, zone).year === dayIn(now, zone).year;
    return formatter(zone, sameYear ? { month: 'short', day: 'numeric', ...TIME } : { month: 'short', day: 'numeric', year: 'numeric', ...TIME }).format(date);
  }
  const days = daysFrom(date, now, zone);
  if (days === 0) return time;
  if (days === -1) return options.dateOnly ? 'Yesterday' : `Yesterday ${time}`;
  if (days === 1) return options.dateOnly ? 'Tomorrow' : `Tomorrow ${time}`;
  if (dayIn(date, zone).year === dayIn(now, zone).year) {
    return formatter(zone, options.dateOnly ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', ...TIME }).format(date);
  }
  return formatter(zone, options.withTime ? { month: 'short', day: 'numeric', year: 'numeric', ...TIME } : { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

/** The whole of it, for a hover: "Wed, Oct 21, 2026, 2:41 PM EDT". */
export function formatFull(at: When, options: Pick<FormatOptions, 'zone'> = {}): string {
  const date = toDate(at);
  if (Number.isNaN(date.getTime())) return '';
  return formatter(options.zone ?? displayTimeZone(), {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...TIME, timeZoneName: 'short',
  }).format(date);
}

/** A day with no time: "Oct 18, 2026". */
export function formatDay(at: When, options: Pick<FormatOptions, 'zone'> = {}): string {
  const date = toDate(at);
  if (Number.isNaN(date.getTime())) return '';
  return formatter(options.zone ?? displayTimeZone(), { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

/**
 * A moment to the second, with its zone and its day (Jason, 25 Oct): "10:49:25 PM EST on
 * Oct 24", and the year too when it is not this one, "on Oct 24, 2026".
 */
export function formatStamp(at: When, options: Pick<FormatOptions, 'zone' | 'now'> = {}): string {
  const date = toDate(at);
  if (Number.isNaN(date.getTime())) return '';
  const zone = options.zone ?? displayTimeZone();
  const clock = formatter(zone, { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short' }).format(date);
  const sameYear = dayIn(date, zone).year === dayIn(options.now ?? new Date(), zone).year;
  const day = formatter(zone, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  return `${clock} on ${day}`;
}

/**
 * A clock time to the millisecond, for console and network rows:
 * "2:41:07.215 PM"; to the second with `ms: false` ("2:41:07 PM").
 */
export function formatClock(at: When, options: Pick<FormatOptions, 'zone'> & { ms?: boolean } = {}): string {
  const date = toDate(at);
  if (Number.isNaN(date.getTime())) return '';
  return formatter(options.zone ?? displayTimeZone(), {
    hour: 'numeric', minute: '2-digit', second: '2-digit', ...(options.ms === false ? {} : { fractionalSecondDigits: 3 }),
  }).format(date);
}

const DAY_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The first moment of a `YYYY-MM-DD` day in the person's zone (or `zone`):
 * its midnight there, or, in a zone whose clocks change at midnight (Havana,
 * Santiago), the moment they reach next, 1 AM (Codex, round 1). The same
 * search as any wall time (z.ai, round 4). Null for a string that is not a day.
 */
export function midnightIn(day: string, zone: string = displayTimeZone()): Date | null {
  return DAY_SHAPE.test(day) ? wallTimeIn(`${day}T00:00:00`, zone) : null;
}

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** How far `zone`'s clock is ahead of UTC at `instant`, in milliseconds. */
function zoneOffset(instant: number, zone: string): number {
  let parts = offsetFormatters.get(zone);
  if (!parts) {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, numberingSystem: 'latn', hourCycle: 'h23',
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
    });
    offsetFormatters.set(zone, parts);
  }
  const at = parts.formatToParts(new Date(instant));
  const value = (type: string) => Number(at.find((part) => part.type === type)?.value);
  return Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second')) - Math.floor(instant / 1000) * 1000;
}

const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/** A time that names its own moment: an ISO time ending in Z or an offset. */
const NAMED_MOMENT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * A wall-clock time a filter holds ("2026-10-21T14:30", or a day) as the
 * moment it is in the person's zone, the one the times beside it are shown in
 * (Codex, TOADO-904 round 2); a string that already names its moment (an ISO
 * time with an offset) is that moment. Null for anything else, including a
 * zone-less string of another shape, which the browser would read in its own
 * zone (z.ai, round 4), and a zone this browser does not know.
 */
export function wallTimeIn(value: string, zone: string = displayTimeZone()): Date | null {
  const named = namedMoment(value);
  if (named !== undefined) return named;
  const passes = wallPasses(value, zone);
  return passes ? new Date(passes.first) : null;
}

/**
 * The last moment a filter's end includes, in the person's zone: the end of
 * the second it names, at its last pass where the clocks repeat it (the last
 * hour of 24 October in Nuuk is read twice: Codex, round 5); for a day alone,
 * the end of that day (z.ai, round 5); for a time the clocks skip, the moment
 * before they reach next. The picker writes an end as 23:59:59, and a ticket
 * made half a second later is still on that day (z.ai, round 4).
 */
export function wallTimeEndIn(value: string, zone: string = displayTimeZone()): Date | null {
  const named = namedMoment(value);
  if (named !== undefined) return named;
  // A day ends with its last second's last pass, which, where the clocks go
  // back in the evening, comes after the next day's first midnight
  // (Goose Bay, 31 October 2009: Codex, round 6).
  if (DAY_SHAPE.test(value)) return wallTimeEndIn(`${value}T23:59:59`, zone);
  const passes = wallPasses(value, zone);
  if (!passes) return null;
  return new Date(passes.skipped ? passes.first - 1 : passes.last + 999);
}

/** A string that names its own moment, as that moment; null for a malformed one; undefined for any other string. */
function namedMoment(value: string): Date | null | undefined {
  if (WALL_TIME.test(value)) return undefined;
  const date = NAMED_MOMENT.test(value) ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

/**
 * The seconds at which a wall time's clock reading falls in `zone`: its first
 * and last pass (the same second unless the clocks repeat it), or, for a time
 * the clocks skip, the moment they reach next, as `first`. Null for a string
 * that is not a wall time, one Date.UTC would roll over ("2026-02-31"), or a
 * zone this browser does not know.
 */
function wallPasses(value: string, zone: string): { first: number; last: number; skipped: boolean } | null {
  const match = WALL_TIME.exec(value);
  if (!match || !isKnownTimeZone(zone)) return null;
  const [, y, mo, d, h = '0', mi = '0', s = '0'] = match;
  const parts = [y, mo, d, h, mi, s].map(Number) as [number, number, number, number, number, number];
  const wall = Date.UTC(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
  if (Number.isNaN(wall) || new Date(wall).toISOString().slice(0, 19) !== `${y}-${mo}-${d}T${h.padStart(2, '0')}:${mi.padStart(2, '0')}:${s.padStart(2, '0')}`) return null;
  // The first second whose wall clock there reads this time or later: found by
  // search, not by an offset, because a time the clocks skip (midnight in
  // Havana in March) does not exist and is the moment the clocks reach next,
  // and a time they repeat is its first (Codex, round 3).
  const target = wallNumber(parts);
  let low = wall - 30 * 3_600_000;
  let high = wall + 30 * 3_600_000;
  while (high - low > 1_000) {
    const middle = low + Math.floor((high - low) / 2_000) * 1_000;
    if (wallNumber(wallPartsAt(middle, zone)) >= target) high = middle;
    else low = middle;
  }
  // The clock is not monotone through a time it repeats, so the search can
  // land on any pass. Each pass is the time read under one of the offsets in
  // force around it, so the seconds that read it under any of them are all
  // its passes, however far the clocks go back (an hour in New York, half an
  // hour at Lord Howe, two hours at Troll: Codex, round 4). A time the clocks
  // skip reads under none, and stays the moment they reach next.
  // The offsets sampled are those in force 30 hours either side and either
  // side of where the search landed: enough while a zone changes its clocks
  // at most once in 60 hours, which every zone in tzdata does (z.ai, round 6).
  const offsets = new Set([wall - 30 * 3_600_000, wall + 30 * 3_600_000, high, high - 1_000].map((at) => zoneOffset(at, zone)));
  const passes = [...offsets].map((offset) => wall - offset)
    .filter((pass) => wallNumber(wallPartsAt(pass, zone)) === target);
  if (passes.length === 0) return { first: high, last: high, skipped: true };
  return { first: Math.min(...passes), last: Math.max(...passes), skipped: false };
}

/** A wall time as one comparable number: 20261021143000. */
function wallNumber([year, month, day, hour, minute, second]: readonly number[]): number {
  return ((((year! * 100 + month!) * 100 + day!) * 100 + hour!) * 100 + minute!) * 100 + second!;
}

/** The wall clock in `zone` at `instant`. */
function wallPartsAt(instant: number, zone: string): number[] {
  const offset = zoneOffset(instant, zone);
  const wall = new Date(Math.floor(instant / 1000) * 1000 + offset);
  return [wall.getUTCFullYear(), wall.getUTCMonth() + 1, wall.getUTCDate(), wall.getUTCHours(), wall.getUTCMinutes(), wall.getUTCSeconds()];
}

/** The day after a `YYYY-MM-DD` day, as one. */
export function dayAfter(day: string): string {
  const match = DAY_SHAPE.exec(day);
  if (!match) return day;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1)).toISOString().slice(0, 10);
}

/** How long since `at`, for a duration ("3 days", "5 hours"); never "ago". */
export function durationSince(at: When, now: Date = new Date()): string {
  const date = toDate(at);
  if (Number.isNaN(date.getTime())) return '';
  if (now.getTime() - date.getTime() < 60_000) return 'under a minute';
  return formatDistanceStrict(date, now);
}
